"use client"

import React, { JSX, memo } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps, cn } from "../../utils"
import { Slider } from "../shadcn/slider"

/**
 * @description 범위 슬라이더. value는 number 배열 (단일: [50], 범위: [min, max])
 * @example
 * ```tsx
 * <TSlider value={[brightness]} min={0} max={100} onChange={([v]) => setBrightness(v)} />
 * ```
 */
interface TSliderProps extends BaseInteractiveProps {
	/** 슬라이더 input name 속성 */
	name?: string
	/** 슬라이더 값 배열. 단일 값: [50], 범위: [20, 80] */
	value: Array<number>
	/**
	 * 최솟값
	 * @default 0
	 */
	min?: number
	/**
	 * 최댓값
	 * @default 100
	 */
	max?: number
	/**
	 * 값 변경 단위
	 * @default 1
	 */
	step?: number
	/** 왼쪽 라벨 */
	leftLabel?: React.ReactNode
	/** 오른쪽 라벨 */
	rightLabel?: React.ReactNode
	/** 슬라이더 thumb의 접근성 이름 (스크린 리더용). 시각적 라벨(leftLabel/rightLabel)과 별개로 필요 */
	ariaLabel?: string
	/** thumb 값을 사람이 읽는 형태로 전달 (예: "50%", "1.2초") */
	ariaValueText?: string
	/** thumb hover에 표시할 문자열. 슬라이더 제어값과 변경 콜백에는 원본 숫자를 유지한다. */
	formatValue?: (value: number, index: number) => string
	/**
	 * 라벨 배치 위치
	 * @default "side"
	 * @ai-style "side" - 슬라이더 양 옆에 라벨 배치
	 * @ai-style "bottom" - 슬라이더 하단에 라벨 배치
	 */
	labelPlacement?: "side" | "bottom"
	/** 값 변경 콜백. number 배열을 전달 */
	onChange: (value: Array<number>) => void
	/** 드래그 완료(커밋) 시 콜백. 슬라이더에서 손을 뗄 때 최종 값 배열 전달 */
	onValueCommit?: (value: Array<number>) => void
}

function TSliderInner(props: TSliderProps): JSX.Element {
	const {
		id,
		name,
		value,
		min = 0,
		max = 100,
		step = 1,
		leftLabel,
		rightLabel,
		ariaLabel,
		ariaValueText,
		formatValue,
		labelPlacement = "side",
		disabled,
		className,
		dataAttributes,
		onChange,
		onValueCommit,
		...restProps
	} = props
	const dataProps = useDataAttributes(dataAttributes)

	const slider = React.useMemo(
		() => (
			<div className="slider">
				<Slider
					id={id}
					name={name}
					value={value}
					min={min}
					max={max}
					step={step}
					disabled={disabled}
					onValueChange={onChange}
					onValueCommit={onValueCommit}
					className={cn(labelPlacement === "side" ? "grow" : "w-full")}
					ariaLabel={ariaLabel}
					ariaValueText={ariaValueText}
					formatValue={formatValue}
					{...dataProps}
				/>
			</div>
		),
		[id, value, min, max, step, disabled, labelPlacement, dataProps, onChange, onValueCommit, ariaLabel, ariaValueText, formatValue]
	)

	return (
		<div className={cn("TSlider", className)} {...restProps} data-label-placement={labelPlacement}>
			{labelPlacement === "side" ? (
				<div className="side">
					{leftLabel && <span className="label">{leftLabel}</span>}
					{slider}
					{rightLabel && <span className="label">{rightLabel}</span>}
				</div>
			) : (
				<>
					{slider}
					<div className="bottom">
						{leftLabel && <span className="label">{leftLabel}</span>}
						{rightLabel && <span className="label">{rightLabel}</span>}
					</div>
				</>
			)}
		</div>
	)
}

TSliderInner.displayName = "TSlider"

export const TSlider = memo(TSliderInner)
